import { webCoreTypes } from "@core/core-types.di";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type {
    HttpErrorResponse,
    IotApiClient,
    ModelError,
} from "@iot-platform/core";
import { HttpFailedRequestError } from "@iot-platform/core";
import type { WeatherDto } from "@weather/data/dto/weather.dto";
import { WeatherMapper } from "@weather/data/mappers/weather.mapper";
import { WeatherNoCoordsError } from "@weather/domain/errors/weather-no-coords.error";
import type { Weather } from "@weather/domain/models/weather.model";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";

@injectable()
export class WeatherDatasource {
    static readonly WEATHER_PATH = "/stations/:stationId/weather_forecast";

    constructor(
        @inject(webCoreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(WeatherMapper)
        private readonly weatherMapper: WeatherMapper,
    ) {}

    fetchAll(
        stationId: string,
    ): ResultAsync<
        Weather,
        ModelError<typeof Weather> | WeatherNoCoordsError | FallbackError
    > {
        return this.iotApiClient.http
            .get<WeatherDto>(
                WeatherDatasource.WEATHER_PATH.replace(":stationId", stationId),
            )
            .mapErr((httpError) => {
                if (
                    httpError instanceof HttpFailedRequestError &&
                    (httpError.data as HttpErrorResponse).error ===
                        "No coordinates in station"
                ) {
                    return new WeatherNoCoordsError();
                }
                return new FallbackError();
            })
            .andThen((response) => this.weatherMapper.map(response.data));
    }
}
